import React, { useMemo } from 'react';
import gql from 'graphql-tag';

import {
  FieldInput,
  FilterProps,
  TableCellV8,
  SelectColumnV8,
  CellDateV8,
  FormFilterCard,
  FieldDateRanger,
  FormCol4,
  buildWhereForDates,
} from 'motion-components';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import { FieldSelectNomos, FieldSelectRegion } from 'shared-pages';
import { CollectionOrderStatusBadge } from 'shared-pages/pages/collection-orders/collection-order-status-badge';
import { promiseModalMultiSelect } from 'motion-components';
import { isAfisCyprus, isMedicycle } from 'shared-pages/common/constants';

// -------------------------------------------------------------------------------
// Modal Select Collection Orders
// -------------------------------------------------------------------------------

const gqlGet = gql`
  query getCollectionOrders($limit: Int, $offset: Int, $order: String, $where: String) {
    getCollectionOrders(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        statusId
        code
        seriesId
        codePrefix
        codeNumber
        orderedDate
        collectionPointId
        collectorId
        legacyAppId
        recordDate
        isActive
        firstDate
        lastDate
        firstUser
        lastUser
        contactPerson
        notes
        userString1
        userString2
        userDate1
        collectionpoint {
          id
          name
          addressLine1
          addressArea
          addressCity
          addressDistrict
          phone
          regionId
          addressNomos
          region {
            id
            name
          }
        }
        collector {
          id
          name
        }
        collectionOrdersContainers {
          id
          containertypeId
          containersQty
          containertype {
            id
            name
          }
        }
        collectionOrderState {
          id
          name
        }
      }
      recordCount
    }
  }
`;

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values) => {
  // Φτιάξε το where για το graphql
  let where = {};

  if (values) {
    // console.log('buildWhereFromFilters values', values);
    where = {
      ...where,
      collectorId: values.collectorId,
    };
    if (values.searchText) {
      const searchText = '%' + values.searchText + '%';
      const likeObj = { $like: searchText };
      where = {
        ...where,
        $or: [{ code: likeObj }, { '$collectionpoint.name$': likeObj }],
      };
    }
    if (values.isActive !== undefined && values.isActive !== null && values.isActive !== '') {
      where = { ...where, isActive: values.isActive };
    }
    if (values.startDate || values.endDate) {
      where = {
        ...where,
        ...buildWhereForDates(values.startDate, values.endDate, 'orderedDate'),
      };
    }
    if (values.regionId) {
      where = { ...where, regionId: values.regionId };
    }
    if (values.wasteOriginTypeId) {
      where = { ...where, wasteOriginTypeId: values.wasteOriginTypeId };
    }

    if (values.wasteDestinationTypeId) {
      where = { ...where, wasteDestinationTypeId: values.wasteDestinationTypeId };
    }

    if (values.addressNomos) {
      where = { ...where, addressNomos: values.addressNomos };
    }

    if (values.statusId) {
      where = { ...where, statusId: values.statusId };
    }
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  const { t } = useTranslation();

  const filters = {
    searchText: initialValues?.searchText ?? '',
    isActive: initialValues?.isActive ?? null,
    regionId: initialValues?.regionId ?? undefined,
    wasteOriginTypeId: initialValues?.wasteOriginTypeId ?? undefined,
    wasteDestinationTypeId: initialValues?.wasteDestinationTypeId ?? undefined,
    addressNomos: initialValues?.addressNomos ?? undefined,
    startDate: initialValues?.startDate ?? null,
    endDate: initialValues?.endDate ?? null,
    statusId: initialValues?.statusId ?? null,
    collectorId: initialValues?.collectorId ?? null,
  };
  return (
    <FormFilterCard title={t('Κριτήρια Αναζήτησης')} onSubmit={onChange} initialValues={filters}>
      <Row>
        <FieldInput
          as={FormCol4}
          name="searchText"
          label={t('Αναζήτηση')}
          type="search"
          placeholder={t('Κωδικός Αιτήματος ή Επωνυμία Σημείου Συλλογής')}
        />
        <FieldDateRanger as={FormCol4} label={t('Υποβολή από')} name="startDate" name2="endDate" />
        {isAfisCyprus || isMedicycle ? (
          <FieldSelectRegion as={FormCol4} />
        ) : (
          <FieldSelectNomos as={FormCol4} />
        )}

        {/* <FieldSelectIsActive label={t('Ενεργές Εγγραφές')} /> */}
      </Row>
    </FormFilterCard>
  );
};

// -------------------------------------------------------------------------------
const s = typeof SelectColumnV8 === 'function' ? SelectColumnV8() : SelectColumnV8;
const columns: Array<any> = [
  s,
  {
    id: 'code',
    header: 'Κωδικός Αιτήματος',
    columnName: 'Απόβλητα',
    accessorKey: 'code',
    orderByField: 'code',
    cell: TableCellV8,
    size: 15,
  },
  {
    id: 'orderedDate',
    header: i18n.t('Ημερομηνία Αιτήματος'),
    columnName: i18n.t('Ημερομηνία Αιτήματος'),
    accessorKey: 'orderedDate',
    orderByField: 'orderedDate',
    cell: CellDateV8,
    size: 50,
    meta: { type: 'text' },
  },
  {
    id: 'collectionPointId',
    header: i18n.t('Σημείο Συλλογής'),
    columnName: i18n.t('Σημείο Συλλογής'),
    accessorKey: 'collectionpoint.name',
    orderByField: 'collectionpoint.name',
    cell: ({ getValue, row }) => {
      const cp = row.original.collectionpoint;
      return (
        <div className="text-start">
          <div>{cp.name}</div>
          <div style={{ color: 'gray', fontSize: 'smaller' }}>
            {cp.addressLine1}
            {cp.addressArea && <>,&nbsp;{cp.addressArea}</>}
            {cp.addressCity && <>,&nbsp;{cp.addressCity}</>}
            {cp.addressDistrict && <>,&nbsp;{cp.addressDistrict}</>}
            {cp.addressNomos && <>,&nbsp;{cp.addressNomos}</>}
            {cp.region?.name && <>,&nbsp;{cp.region?.name}</>}
            {/* {isAfisCyprus || isMedicycle ? <>&nbsp;{cp.region?.name}</> : <></>} */}
          </div>
        </div>
      );
    },
    size: 35,
    meta: { type: 'text' },
  },
  {
    id: 'contactPerson',
    header: i18n.t('Πρόσωπο Επικοινωνίας & Τηλ. / Σημειώσεις'),
    columnName: i18n.t('Συλλέκτης'),
    accessorKey: 'contactPerson',
    orderByField: 'contactPerson',
    cell: ({ getValue, row }) => (
      <div className="text-start">
        <div>
          {row.original.contactPerson ? `${row.original.contactPerson} - ` : ``}{' '}
          {row.original.collectionpoint.phone}
        </div>
        <div style={{ color: 'gray', fontSize: 'smaller' }}>{row.original.notes}</div>
      </div>
    ),
    size: 30,
    meta: { type: 'text' },
  },
  {
    id: 'collectionOrdersContainers',
    header: i18n.t('Συσκευασίες'),
    columnName: i18n.t('Συσκευασίες'),
    accessorKey: 'collectionOrdersContainers',
    orderByField: 'collectionOrdersContainers',
    cell: ({ getValue, row }) => {
      const containers = row.original.collectionOrdersContainers;
      return (
        <div className="text-start">
          {containers.map((v, i) => {
            if (v.containersQty && v.containersQty > 0)
              return (
                <div key={v.id || i}>
                  {v.containersQty} {v.containertype?.name}
                </div>
              );
          })}
        </div>
      );
    },
    size: 20,
  },
  {
    id: 'collectionOrderState.name',
    header: i18n.t('Κατάσταση Αιτήματος'),
    columnName: i18n.t('Κατάσταση Αιτήματος'),
    accessorKey: 'collectionOrderState.name',
    orderByField: 'collectionOrderState.name',
    cell: ({ row }) => {
      const data = row.original;
      return (
        <CollectionOrderStatusBadge id={data?.statusId} name={data?.collectionOrderState?.name} />
      );
    },
    size: 10,
    meta: { type: 'text' },
  },
];

// -------------------------------------------------------------------------------
export const promiseModalSelectMultiCollectionOrders = (props) => {
  return promiseModalMultiSelect({
    ...props,
    title: i18n.t('Επιλογή Αιτημάτων Συλλογής'),

    gqlQuery: gqlGet,
    columns: columns,

    buildWhereFromFilters: buildWhereFromFilters,
    initFilters: props.initFilters,
    Filters: Filters,
    getRowId: (row) => row.id,
    idFieldName: 'id',
    nameFieldName: 'code',
    orderBy: 'code',
    autoFetchData: true,
    size: 'xl',
  });
};
// -------------------------------------------------------------------------------
